import { useMemo } from "react";

import { DashboardCommsToSendSection } from "./DashboardCommsToSendSection";
import { DashboardScheduleEditAvailabilityButton } from "./DashboardScheduleEditAvailabilityButton";
import { DashboardSchedulePanelViewToggle } from "./DashboardSchedulePanelViewToggle";
import { DashboardTaskCategorySection } from "./DashboardTaskCategorySection";
import { DashboardTaskDayNav } from "./DashboardTaskDayNav";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";

import { useCreateAddDashboardTask } from "@/hooks/createAdds/useCreateAddDashboardTask";
import { dashboardScheduleWeek } from "@/lib/dashboardScheduleWeek";
import {
  CommsToSendCandidacy,
  DashboardScheduleTaskCategory,
  DashboardSchedulePanelView,
  DashboardTask,
} from "@/types";

export const TasksContainerExpanded = ({
  tasks,
  commsToSend,
  day,
  onDayChange,
  panelView,
  onPanelViewChange,
  onEditAvailability,
  showTaskOwnerAvatars = false,
  newTaskAssigneeUserId,
}: {
  tasks: DashboardTask[];
  commsToSend: CommsToSendCandidacy[];
  day: Date;
  onDayChange: (day: Date) => void;
  panelView: DashboardSchedulePanelView;
  onPanelViewChange: (view: DashboardSchedulePanelView) => void;
  onEditAvailability: () => void;
  showTaskOwnerAvatars?: boolean;
  newTaskAssigneeUserId: number | null;
}) => {
  /* Database CRUD Operations */
  // Create
  const { mutate: createTask } = useCreateAddDashboardTask();

  /* React Hooks */
  const dayTasks = useMemo(
    () => dashboardScheduleWeek.tasksForDay(tasks, day),
    [tasks, day],
  );

  /* Handlers */
  // Quick-add a general task on the viewed day, owned by the viewed staffer.
  const handleAddTask = (title: string) => {
    if (newTaskAssigneeUserId == null) return;
    createTask({
      title,
      dueDate: day.toISOString(),
      assignees: [newTaskAssigneeUserId],
    });
  };

  /* Render */
  const render = () => (
    <Column className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-lever-border-light bg-lever-surface-default">
      <Row className="h-11 shrink-0 items-center border-b border-lever-border-light bg-lever-surface-default-on-elevation px-3">
        <DashboardTaskDayNav
          day={day}
          onDayChange={onDayChange}
          variant="comfortable"
        />
      </Row>
      <Column className="min-h-0 flex-1 overflow-y-auto">
        {dashboardScheduleWeek.listCategoriesForDay(day).map((category) => (
          <DashboardTaskCategorySection
            key={category}
            category={category}
            day={day}
            dayTasks={dayTasks}
            onAddTask={
              category === DashboardScheduleTaskCategory.GENERAL
                ? handleAddTask
                : undefined
            }
            showOwnerAvatar={showTaskOwnerAvatars}
            variant="comfortable"
          />
        ))}
        <DashboardCommsToSendSection
          candidacies={commsToSend}
          variant="comfortable"
        />
      </Column>
      <Row className="shrink-0 items-center justify-between border-t border-lever-border-light px-3 py-2.5">
        <DashboardScheduleEditAvailabilityButton
          onClick={onEditAvailability}
          variant="labeled"
        />
        <DashboardSchedulePanelViewToggle
          value={panelView}
          onChange={onPanelViewChange}
        />
      </Row>
    </Column>
  );

  return render();
};
